import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
    ICreatePostCommentModel,
    ICreatePostModel,
    IPostCategoryModel,
    IPostCommentModel,
    IPostListingFilterModel,
    IPostModel,
    IReportPostModel,
    ISaveListModel,
    ISharePostModel,
} from '../../../models/post.model';
import { BaseApiService } from '../../base-api/base-api.service';

@Injectable({
    providedIn: 'root',
})
export class PostService extends BaseApiService {
    protected override endPoint = '/posts';

    getPostsListing(filter: IPostListingFilterModel = {}): Observable<IPostModel[]> {
        const params = {
            privacy: filter.privacy ?? 'all',
            type: filter.type ?? 'all',
            sort: filter.sort ?? 'latest',
            page: filter.page ?? 1,
            size: filter.size ?? 20,
            ...(filter.criteria ? { criteria: filter.criteria } : {}),
            ...(filter.keyword ? { keyword: filter.keyword } : {}),
            ...(filter.tag ? { tag: filter.tag } : {}),
            ...(filter.cats ? { cats: filter.cats } : {}),
        };

        return this.http.get<IPostModel[]>(`${this.fullPath}`, { params });
    }

    getPostDetail(postId: number): Observable<IPostModel> {
        return this.http.get<IPostModel>(`${this.fullPath}/${postId}`);
    }

    getPostCategoryListing(pageSize: number = 10, keyword?: string): Observable<IPostCategoryModel[]> {
        const params = {
            page: 1,
            size: pageSize,
            ...(keyword ? { keyword } : {}),
        };

        return this.http.get<IPostCategoryModel[]>(`${this.fullPath}/cats`, { params });
    }

    giveBadgeToPost(postId: number, badgeId: number): Observable<void> {
        return this.http.post<void>(`${this.fullPath}/${postId}/badge/${badgeId}`, {});
    }

    hidePost(postId: number): Observable<void> {
        return this.http.post<void>(`${this.fullPath}/${postId}/hide`, {});
    }

    createPost(data: ICreatePostModel): Observable<IPostModel> {
        const payload = {
            content: data.content,
            type: data.type,
            ...(data.privacy ? { privacy: data.privacy } : {}),
            ...(data.photo ? { photo: data.photo } : {}),
            ...(data.cats && data.cats.length ? { cats: data.cats } : {}),
            ...(data.verses && data.verses.length ? { verses: data.verses } : {}),
            ...(data.url ? { url: data.url } : {}),
            ...(data.group ? { group: data.group } : {}),
        };

        return this.http.post<IPostModel>(`${this.fullPath}`, payload);
    }

    editPost(postId: number, data: ICreatePostModel): Observable<IPostModel> {
        const payload = {
            content: data.content,
            type: data.type,
            privacy: data.privacy,
            ...(data.photo ? { photo: data.photo } : {}),
            ...(data.cats && data.cats.length ? { cats: data.cats } : {}),
            ...(data.verses && data.verses.length ? { verses: data.verses } : {}),
            ...(data.url ? { url: data.url } : {}),
        };

        return this.http.put<IPostModel>(`${this.fullPath}/${postId}`, payload);
    }

    quotePost(postId: number, data: ICreatePostModel): Observable<IPostModel> {
        const payload = {
            content: data.content,
            type: data.type,
            privacy: data.privacy,
            ...(data.photo ? { photo: data.photo } : {}),
            ...(data.cats && data.cats.length ? { cats: data.cats } : {}),
            ...(data.verses && data.verses.length ? { verses: data.verses } : {}),
            ...(data.url ? { url: data.url } : {}),
        };

        return this.http.post<IPostModel>(`${this.fullPath}/${postId}/quote`, payload);
    }

    likePost(postId: number): Observable<void> {
        return this.http.post<void>(`${this.fullPath}/${postId}/like`, {});
    }

    unlikePost(postId: number): Observable<void> {
        return this.http.delete<void>(`${this.fullPath}/${postId}/like`);
    }

    markPostAnswered(postId: number, testimony: string | null): Observable<void> {
        const payload = { testimony };
        return this.http.post<void>(`${this.fullPath}/${postId}/answer`, payload);
    }

    deleteComment(commentId: number): Observable<void> {
        return this.http.delete<void>(`${this.apiServer}/comments/${commentId}`);
    }

    deletePost(postId: number): Observable<void> {
        return this.http.delete<void>(`${this.fullPath}/${postId}`);
    }

    editComment(commentId: number, data: ICreatePostCommentModel): Observable<IPostCommentModel> {
        const payload = {
            content: data.content,
            ...(data.attachment ? { attachment: data.attachment } : {}),
            ...(data.attachment ? { contentType: 'IMAGE' } : {}),
            ...(data.verses && data.verses.length ? { verses: data.verses } : {}),
            ...(data.comment ? { comment: data.comment } : {}),
        };

        return this.http.put<IPostCommentModel>(`${this.apiServer}/comments/${commentId}`, payload);
    }

    savePost(postId: number, saveListId: number): Observable<void> {
        return this.http.post<void>(`${this.fullPath}/${postId}/save/${saveListId}`, {});
    }

    getSaveListing(filter: IPostListingFilterModel = {}): Observable<ISaveListModel[]> {
        const params = {
            page: filter.page ?? 1,
            size: filter.size ?? 999,
            all: true,
        };
        return this.http.get<ISaveListModel[]>(`${this.apiServer}/savelist`, { params });
    }

    getSavedPostsListing(saveListId: number, filter: IPostListingFilterModel = {}): Observable<IPostModel[]> {
        const params = {
            page: filter.page ?? 1,
            size: filter.size ?? 20,
        };
        return this.http.get<IPostModel[]>(`${this.apiServer}/savelist/${saveListId}/posts`, { params });
    }

    getPostComments(postId: number, filter: IPostListingFilterModel = {}): Observable<IPostCommentModel[]> {
        const params = {
            page: filter.page ?? 1,
            size: filter.size ?? 999,
        };

        return this.http.get<IPostCommentModel[]>(`${this.fullPath}/${postId}/comments`, { params });
    }

    likePostComment(commentId: number): Observable<void> {
        return this.http.post<void>(`${this.apiServer}/comments/${commentId}/like`, {});
    }

    unlikePostComment(commentId: number): Observable<void> {
        return this.http.delete<void>(`${this.apiServer}/comments/${commentId}/like`);
    }

    createPostComment(postId: number, data: ICreatePostCommentModel): Observable<IPostCommentModel> {
        const payload = {
            content: data.content,
            ...(data.attachment ? { attachment: data.attachment } : {}),
            ...(data.attachment ? { contentType: 'IMAGE' } : {}),
            ...(data.verses && data.verses.length ? { verses: data.verses } : {}),
            ...(data.comment ? { comment: data.comment } : {}),
        };

        return this.http.post<IPostCommentModel>(`${this.fullPath}/${postId}/comments`, payload);
    }

    reportPost(postId: number, reason: IReportPostModel): Observable<void> {
        return this.http.post<void>(`${this.fullPath}/${postId}/report`, reason);
    }

    generateSharePostLink(postId: number): Observable<ISharePostModel> {
        return this.http.post<ISharePostModel>(`${this.fullPath}/${postId}/share`, {});
    }
}
